import Label from "@/modules/common/components/ui-kit/label";
import WithResponsiveRendering from "@/modules/common/components/wrappers/with-responsive-rendering";
import UserNameTag from "@/modules/lead-page/new/components/activity/user-name-tag";
import { ParsedLeadMining } from "@/modules/lead-page/new/components/activity/lead-mining/parser";
import LeadMiningSections from "@/modules/lead-page/new/components/activity/lead-mining/sections";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Sparkles, X } from "lucide-react";

interface LeadMiningDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  data: ParsedLeadMining;
  actorName?: string | null;
  actorColor?: string | null;
  createdAt?: string;
}

const useDrawerParts = ({
  data,
  onClose,
  actorName,
  actorColor,
}: LeadMiningDetailsDrawerProps) => {
  const theme = useTheme();

  const hasIntentScore = typeof data.intentScore === "number";
  const hasMeta = !!data.category || !!actorName || hasIntentScore;

  const intentScoreColors = (() => {
    const score = data.intentScore ?? 0;
    if (score >= 80) {
      return {
        bg: theme.nawy.colors.semantic.indicator.greenLight,
        fg: theme.nawy.colors.semantic.indicator.green,
      };
    }
    if (score >= 50) {
      return {
        bg: theme.nawy.colors.semantic.indicator.yellowLight,
        fg: theme.nawy.colors.semantic.indicator.yellowDark,
      };
    }
    return {
      bg: theme.nawy.colors.semantic.indicator.redLight,
      fg: theme.nawy.colors.semantic.indicator.red,
    };
  })();

  const header = (
    <Box
      sx={{
        padding: "18px 22px",
        borderBottom: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
        display: "flex",
        alignItems: "center",
        gap: "12px",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          backgroundColor: theme.nawy.colors.semantic.indicator.greenLight,
          color: theme.nawy.colors.semantic.indicator.green,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Sparkles size={18} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: theme.nawy.colors.semantic.text.primary,
          }}
        >
          Re-engagement details
        </Typography>
        {data.lastAppSession && (
          <Typography
            sx={{
              fontSize: 12,
              color: theme.nawy.colors.semantic.text.tertiary,
            }}
          >
            Last interaction {data.lastAppSession}
          </Typography>
        )}
      </Box>
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          width: 32,
          height: 32,
          border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
          borderRadius: "8px",
          color: theme.nawy.colors.semantic.text.secondary,
          "&:hover": {
            backgroundColor: theme.nawy.colors.semantic.layer.secondary,
          },
        }}
      >
        <X size={14} />
      </IconButton>
    </Box>
  );

  const metaBlock = hasMeta ? (
    <Box
      sx={{
        padding: "16px 22px",
        borderBottom: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
        backgroundColor: theme.nawy.colors.primitives.white[200],
        flexShrink: 0,
      }}
    >
      {actorName && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            mb: data.category || hasIntentScore ? "12px" : 0,
          }}
        >
          <UserNameTag name={actorName} color={actorColor} />
          <Typography
            sx={{
              fontSize: 12,
              color: theme.nawy.colors.semantic.text.tertiary,
            }}
          >
            flagged a re-engagement
          </Typography>
        </Box>
      )}
      {(data.category || hasIntentScore) && (
        <Box
          sx={{
            display: "flex",
            gap: "6px",
            flexWrap: "wrap",
          }}
        >
          {data.category && (
            <Label label={data.category} variant="secondary" color="navy" />
          )}
          {hasIntentScore && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: 11,
                color: intentScoreColors.fg,
                fontWeight: 600,
                padding: "3px 8px",
                borderRadius: "999px",
                backgroundColor: intentScoreColors.bg,
              }}
            >
              <Sparkles size={11} />
              Intent {data.intentScore}/100
            </Box>
          )}
        </Box>
      )}
    </Box>
  ) : null;

  const body = (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        padding: `${theme.nawy.spacing.md} 22px`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.sm,
        }}
      >
        <LeadMiningSections data={data} intentVariant="stacked" />
      </Box>
    </Box>
  );

  return { header, metaBlock, body };
};

const DesktopDrawer = (props: LeadMiningDetailsDrawerProps) => {
  const { open, onClose } = props;
  const { header, metaBlock, body } = useDrawerParts(props);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1400,
        "& .MuiDrawer-paper": {
          width: 480,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {header}
      {metaBlock}
      {body}
    </Drawer>
  );
};

const MobileDrawer = (props: LeadMiningDetailsDrawerProps) => {
  const { open, onClose } = props;
  const theme = useTheme();
  const { header, metaBlock, body } = useDrawerParts(props);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1400,
        "& .MuiDrawer-paper": {
          height: "90vh",
          borderTopLeftRadius: theme.nawy.radius["2xl"],
          borderTopRightRadius: theme.nawy.radius["2xl"],
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {header}
      {metaBlock}
      {body}
    </Drawer>
  );
};

const LeadMiningDetailsDrawer =
  WithResponsiveRendering<LeadMiningDetailsDrawerProps>({
    DesktopComponent: DesktopDrawer,
    MobileComponent: MobileDrawer,
  });

export default LeadMiningDetailsDrawer;
